<template>
    <div ref="kpiListContainer" class="kpi-list__container">
        <div ref="kpiListWrapper" class="kpi-list__wrapper">
            <template v-if="kpis.length">
                <div
                    v-for="(group, index) in getKPIMapGroups"
                    :key="index"
                    ref="kpiList"
                    :class="listClass"
                >
                    <template v-for="kpi in group">
                        <KPIItem
                            v-if="kpi.type === 'item'"
                            v-bind="getFormattedKpi(kpi)"
                            :key="kpi.name"
                        />

                        <KPIProgressBar
                            v-else-if="kpi.type === 'progress'"
                            v-bind="getFormattedKpi(kpi)"
                            :key="`${kpi.name}-${kpi.type}-${index}`"
                            :label="$t.__('common.labels.progress')"
                            :highLimit="74.99"
                            :lowLimit="24.99"
                        />

                        <KPIWarning
                            v-else-if="kpi.type === 'warning'"
                            :key="`${kpi.name}-${index}`"
                            :label="kpi.formatted"
                        />
                    </template>
                </div>
            </template>
            <template v-else>
                <div v-for="(_, index) in 2" :key="index" ref="kpiList" class="kpi-list">
                    <div v-for="(_, i) in 4" :key="i" class="kpi-list__skeleton-item">
                        <CompassSkeleton class="kpi-list__skeleton--header" />
                        <CompassSkeleton class="kpi-list__skeleton--value" size="large" />
                    </div>
                </div>
            </template>
        </div>

        <div
            v-show="showLeftShadow"
            ref="shadowLeft"
            class="kpi-list__navigation-button kpi-list__navigation-button--left"
        >
            <button class="kpi-list__scroll-button" @click.stop="handleScroll('left')">
                <CompassIcon icon="caret-left" size="extra-small" />
            </button>
        </div>
        <div
            v-show="showRightShadow"
            ref="shadowRight"
            class="kpi-list__navigation-button kpi-list__navigation-button--right"
        >
            <button class="kpi-list__scroll-button" @click.stop="handleScroll('right')">
                <CompassIcon icon="caret-right" size="extra-small" />
            </button>
        </div>
    </div>
</template>
<script>
import KPIItem from 'vcomponents/common/kpi/KPIItem.vue';
import KPIProgressBar from 'vcomponents/common/kpi/KPIProgressBar.vue';
import KPIMapping from 'Dictionaries/kpi.js';
import KPIWarning from 'vcomponents/common/kpi/KPIWarning.vue';
import { debounce } from 'lodash';

const SOURCES = Object.freeze({
    INVOICE: 'invoice',
    ESTIMATE: 'estimate',
    ORDER: 'order',
    DELIVERY: 'delivery',
    PROFORMA: 'proforma',
    CATALOGUEITEM: 'catalogueitem',
    CREDITNOTE: 'creditnote',
    PURINVOICE: 'purInvoice',
    PURCREDITNOTE: 'purCreditNote',
    PURORDER: 'purOrder',
    PURDELIVERY: 'purDelivery',
    OPPORTUNITY: 'opportunity',
    AGEDBALANCE: 'agedbalance',
    ACCOUNTINGENTRY: 'accountingEntry',
    PAYMENTS: 'payments',
    DEADLINES: 'deadlines',
    SALESBYTHIRD: 'salesbythird',
    ACTIVITY: 'activity',
});

export default {
    components: {
        KPIItem,
        KPIProgressBar,
        KPIWarning,
    },
    props: {
        /**
         * Each entry must have :
         * + name : It's canonical name, use has key; Required
         * + title : Label above value; Required
         * + formatted : Value to display; Required
         * + tips : help at clic on indicator; Optional
         */
        kpis: {
            type: Array,
            required: true,
            validator: function (indicators) {
                let correct = true;
                indicators.forEach((indicator) => {
                    correct = correct && indicator.hasOwnProperty('name') && indicator.name != '';
                    correct = correct && indicator.hasOwnProperty('title') && indicator.title != '';
                    correct =
                        correct &&
                        indicator.hasOwnProperty('formatted') &&
                        indicator.formatted !== '';
                });

                return correct;
            },
        },
        type: {
            type: String,
            default: 'overview',
            required: true,
            validator: function (value) {
                return ['overview', 'listing'].includes(value);
            },
        },
        source: {
            type: String,
            default: 'estimate',
            required: true,
            validator: function (value) {
                return Object.values(SOURCES).includes(value);
            },
        },
    },
    data() {
        return {
            showLeftShadow: false,
            showRightShadow: false,
            observer: null,
        };
    },
    computed: {
        listClass() {
            return ['kpi-list', this.hasWarning ? 'kpi-list--has-warning' : ''];
        },
        kpiMap() {
            return KPIMapping[this.type].find((kpi) => kpi.source === this.source);
        },
        hasWarning() {
            return this.kpis.find((kpi) => kpi.name === 'warning');
        },
        getKPIMapGroups() {
            if (!this.kpiMap || !this.kpis) {
                return [];
            }

            if (this.hasWarning) {
                return [
                    [
                        {
                            id: this.kpis[0].name,
                            type: 'warning',
                            formatted: this.kpis[0].formatted,
                        },
                    ],
                ];
            }

            const filteredGroups = this.kpiMap.groups.map((group) =>
                group.filter((kpi) => this.kpis.some((item) => item.name === kpi.name)),
            );
            return filteredGroups.filter((group) => group.length > 0);
        },
    },
    mounted() {
        this.initObserver();
        this.$refs.kpiListWrapper.addEventListener('scroll', this.onScroll);
    },
    beforeUnmount() {
        if (this.observer) this.observer.unobserve(this.$refs.kpiListContainer);
        this.$refs.kpiListWrapper?.removeEventListener('scroll', this.onScroll);
    },
    methods: {
        onScroll: debounce(function () {
            this.toggleArrowVisibilty();
        }, 100),
        onResize() {
            this.toggleArrowVisibilty();
        },
        initObserver() {
            const observer = new ResizeObserver(this.onResize);
            observer.observe(this.$refs.kpiListContainer);
            this.observer = observer;
        },
        getFormattedKpi(kpi) {
            if (!this.kpiMap || !this.kpis) {
                return kpi;
            }

            const kpiToReturn = this.kpis.find((item) => item.name === kpi.name);

            if (!kpiToReturn) {
                return;
            }

            if (kpi.type === 'progress') {
                return {
                    id: kpiToReturn.name,
                    label: kpiToReturn.title,
                    percentage: parseFloat(kpiToReturn.more.raw),
                    amount: kpiToReturn.more.totalInvoicedAmount,
                    total: kpiToReturn.more.totalAmount,
                };
            }

            return {
                id: kpiToReturn.name,
                label: kpiToReturn.title,
                value: kpiToReturn.formatted,
                illustration: kpi.illustration
                    ? `/ressources/logged/img/illustrations/kpis/${kpi.illustration}.svg`
                    : undefined,
                suffix: kpi.suffix ?? '',
                hideTitle: kpi.hideTitle ?? false,
            };
        },
        toggleArrowVisibilty() {
            this.$nextTick(() => {
                if (
                    this.$refs.kpiListWrapper.scrollLeft + this.$refs.kpiListWrapper.clientWidth >=
                    this.$refs.kpiListWrapper.scrollWidth
                ) {
                    this.showRightShadow = false;
                } else {
                    this.showRightShadow = true;
                }

                this.showLeftShadow = this.$refs.kpiListWrapper.scrollLeft > 0;
            });
        },
        checkElementVisibility(element) {
            if (!(element instanceof HTMLElement)) {
                return true;
            }
            const leftShadowWidth = this.$refs.shadowLeft.clientWidth;
            const rightShadowWidth = this.$refs.shadowRight.clientWidth;

            const rect = element.getBoundingClientRect();
            const parent = this.$refs.kpiListContainer.getBoundingClientRect();

            const isVisible =
                rect.left - parent.left >= leftShadowWidth &&
                rect.right - parent.left <=
                    this.$refs.kpiListContainer.clientWidth - rightShadowWidth;
            return isVisible;
        },
        handleScroll(direction) {
            const scrollLeft = this.$refs.kpiListWrapper.scrollLeft;
            const kpiLists = this.$refs.kpiList;
            const leftShadowWidth = this.$refs.shadowLeft.clientWidth;
            const rightShadowWidth = this.$refs.shadowRight.clientWidth;
            const containerWidth = this.$refs.kpiListContainer.clientWidth;

            if (direction === 'left') {
                // Get first visible list, then take the list n-1 and send it to the right
                for (const [index, kpiList] of kpiLists.entries()) {
                    const previousButton = kpiLists[index - 1];

                    if (this.checkElementVisibility(kpiList) && index > 0) {
                        this.$refs.kpiListWrapper.scrollLeft =
                            previousButton.offsetLeft -
                            (containerWidth - previousButton.clientWidth) +
                            leftShadowWidth;
                        break;
                    }
                }
            } else {
                // Get first element on right not visible and send it to the left
                for (const kpiList of kpiLists) {
                    if (
                        !this.checkElementVisibility(kpiList) &&
                        kpiList.offsetLeft > scrollLeft + leftShadowWidth
                    ) {
                        this.$refs.kpiListWrapper.scrollLeft =
                            kpiList.offsetLeft - rightShadowWidth;
                        break;
                    }
                }
            }

            this.toggleArrowVisibilty();
        },
    },
};
</script>
<style scoped lang="scss">
.kpi-list {
    align-items: end;
    background: var(--colors-common-background-default);
    border-radius: var(--spacing-1);
    box-shadow: var(--elevation-1);
    display: flex;
    gap: var(--spacing-3);
    padding: var(--spacing-2);

    &--has-warning {
        flex: 1;
    }

    &__container {
        margin: calc(var(--spacing-1) * -1) 0 0;
        position: relative;

        &:hover {
            .kpi-list__scroll-button {
                visibility: visible;
            }
        }
    }

    &__wrapper {
        display: flex;
        gap: var(--spacing-3);
        min-width: 100%;
        padding: var(--spacing-1) 0 var(--spacing-2);
        overflow-x: auto;
        overflow-y: hidden;
        overscroll-behavior-y: auto;
        overscroll-behavior-x: contain;
        scroll-behavior: smooth;
        scrollbar-width: none;
    }

    &__navigation-button {
        bottom: 0;
        display: flex;
        padding: 20px 0 0;
        position: absolute;
        top: 0;
        width: 50px;
        z-index: 1;

        &--left {
            background: linear-gradient(270deg, rgba(243, 244, 246, 0) 10%, #f3f4f6 100%);
            justify-content: flex-start;
            left: 0;
        }

        &--right {
            background: linear-gradient(270deg, #f3f4f6 10%, rgba(243, 244, 246, 0) 100%);
            justify-content: flex-end;
            right: 0;
        }
    }

    &__scroll-button {
        align-items: center;
        background-color: var(--colors-common-background-default);
        border: 1px solid var(--colors-common-border-lighter);
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        height: 24px;
        justify-content: center;
        padding: 0;
        visibility: hidden;
        width: 24px;

        &:hover {
            border-color: var(--colors-common-border-default);
        }
    }

    &__skeleton {
        &-item {
            align-items: flex-start;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-0);
            min-width: 120px;
        }

        &--header {
            width: 54px;
        }

        &--value {
            width: 92px;
        }
    }
}
</style>
